import { __awaiter } from "tslib";
import { MediaSession } from 'stanza/jingle';
import { JingleAction, JINGLE_INFO_ACTIVE } from 'stanza/Constants';
import StatsGatherer from 'webrtc-stats-gatherer';
import { EventEmitter } from 'events';
const loggingOverrides = {
    'Discovered new ICE candidate': { skipMessage: true }
};
export class GenesysCloudMediaSession extends MediaSession {
    constructor(params) {
        super(params.options);
        this.iceCandidatesDiscovered = 0;
        this.iceCandidatesReceivedFromPeer = 0;
        this.conversationId = params.conversationId;
        this.fromUserId = params.fromUserId;
        this.originalRoomJid = params.originalRoomJid;
        this.sessionType = params.sessionType;
        this.ignoreHostCandidatesFromRemote = !!params.ignoreHostCandidatesFromRemote;
        this.allowIPv6 = !!params.allowIPv6;
        // babel does not like the typescript recipe for multiple extends so we are hacking this one
        // referencing https://github.com/babel/babel/issues/798
        const eventEmitter = new EventEmitter();
        Object.keys(eventEmitter.__proto__).forEach((name) => {
            this[name] = eventEmitter[name];
        });
        if (!params.optOutOfWebrtcStatsTelemetry) {
            this.setupStatsGatherer();
        }
        this.pc.addEventListener('connectionstatechange', this.onConnectionStateChange.bind(this));
        this.pc.addEventListener('icecandidateerror', this.onIceCandidateError.bind(this));
    }
    getLogDetails() {
        return {
            conversationId: this.conversationId,
            sessionId: this.sid,
            sessionType: this.sessionType
        };
    }
    onTransportInfo(changes, cb) {
        const _super = Object.create(null, {
            onTransportInfo: { get: () => super.onTransportInfo }
        });
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const transport = (_a = changes.contents) === null || _a === void 0 ? void 0 : _a[0].transport;
            const candidates = transport === null || transport === void 0 ? void 0 : transport.candidates;
            if (this.ignoreHostCandidatesFromRemote) {
                const nonHostCandidates = candidates === null || candidates === void 0 ? void 0 : candidates.filter(candidate => candidate.type !== 'host');
                if ((nonHostCandidates === null || nonHostCandidates === void 0 ? void 0 : nonHostCandidates.length) !== ((_b = transport === null || transport === void 0 ? void 0 : transport.candidates) === null || _b === void 0 ? void 0 : _b.length)) {
                    this._log('info', 'Ignoring remote host ice candidates');
                    transport.candidates = nonHostCandidates;
                }
            }
            if (candidates) {
                for (const candidate of candidates) {
                    this._log('info', 'Received candidate from peer', { candidateType: candidate.type });
                    this.iceCandidatesReceivedFromPeer++;
                }
            }
            return _super.onTransportInfo.call(this, changes, cb);
        });
    }
    _log(level, message, ...data) {
        if (this.parent) {
            const redactionInfo = loggingOverrides[message];
            if (redactionInfo === null || redactionInfo === void 0 ? void 0 : redactionInfo.skipMessage) {
                return;
            }
            if (data.length) {
                const firstItem = data[0];
                // if first item is an object, merge the details, else wrap them
                if (typeof firstItem === 'object' &&
                    !Array.isArray(firstItem) &&
                    firstItem !== null) {
                    Object.assign(data[0], this.getLogDetails());
                }
                else {
                    data[0] = Object.assign(Object.assign({}, this.getLogDetails()), { data: firstItem });
                }
            }
            else {
                data.push(this.getLogDetails());
            }
            this.parent.emit('log', level, message, ...data);
            this.parent.emit('log:' + level, message, ...data);
        }
    }
    end(reason = 'success', silent = false) {
        this.state = 'ended';
        this.processingQueue.kill();
        if (typeof reason === 'string') {
            reason = { condition: reason };
        }
        if (!silent) {
            this.send('session-terminate', { reason });
        }
        // After sending session-terminate, wait for the peer connection to die -> if it doesn't, we will manually close it.
        setTimeout(() => {
            if (this.pc.connectionState === 'connected' || this.pc.connectionState === 'connecting') {
                this.pc.close();
            }
        }, 2000);
        this.parent.emit('terminated', this, reason);
        this.parent.forgetSession(this);
    }
    setupStatsGatherer() {
        this.statsGatherer = new StatsGatherer(this.pc);
        this.statsGatherer.on('stats', this.emit.bind(this, 'stats'));
    }
    onIceStateChange() {
        const iceState = this.pc.iceConnectionState;
        const sessionId = this.id;
        const conversationId = this.conversationId;
        this._log('info', 'ICE state changed: ', { iceState, sessionId, conversationId });
        if (iceState === 'connected') {
            this._log('info', 'sending session-info: active');
            this.send(JingleAction.SessionInfo, {
                info: {
                    infoType: JINGLE_INFO_ACTIVE
                }
            });
        }
        else if (iceState === 'failed') {
            this._log('info', 'ICE connection failed', {
                candidatesDiscovered: this.iceCandidatesDiscovered,
                candidatesReceivedFromPeer: this.iceCandidatesReceivedFromPeer
            });
        }
        super.onIceStateChange();
    }
    onConnectionStateChange() {
        this._log('info', 'Connection state changed: ', { connectionState: this.pc.connectionState });
    }
    onIceCandidateError(event) {
        this._log('error', 'IceCandidateError', {
            errorCode: event.errorCode,
            errorText: event.errorText,
            url: event.url
        });
        console.error('IceCandidateError', event);
    }
    onIceCandidate(e) {
        if (e.candidate) {
            if (!this.allowIPv6) {
                const addressRegex = /.+udp [^ ]+ ([^ ]+).*typ host/;
                const matches = addressRegex.exec(e.candidate.candidate);
                const ipv4Regex = /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/;
                if (matches && !matches[1].match(ipv4Regex)) {
                    this._log('debug', 'Filtering out IPv6 candidate', e.candidate.candidate);
                    return;
                }
            }
            // this has too much information and can only be logged locally (debug)
            this._log('debug', 'Processing ice candidate', e.candidate.candidate);
            // this one is info level so it can go to the server
            this._log('info', 'Discovered ice candidate to send to peer', { type: e.candidate.type });
            this.iceCandidatesDiscovered++;
        }
        return super.onIceCandidate(e);
    }
    onIceEndOfCandidates() {
        super.onIceEndOfCandidates();
        this.emit('endOfCandidates');
    }
    addTrack(track, stream) {
        if (track.kind === 'audio') {
            this.includesAudio = true;
        }
        if (track.kind === 'video') {
            this.includesVideo = true;
        }
        return this.processLocal('addtrack', () => __awaiter(this, void 0, void 0, function* () {
            // find an available sender with the correct type
            const availableTransceiver = this.pc.getTransceivers().find((transceiver) => {
                var _a;
                return !transceiver.sender.track && ((_a = transceiver.receiver.track) === null || _a === void 0 ? void 0 : _a.kind) === track.kind;
            });
            if (availableTransceiver) {
                return availableTransceiver.sender.replaceTrack(track);
            }
            this.pc.addTrack(track, stream);
            return;
        }));
    }
}
